import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';

import { LESSON_TYPES } from 'constants/common';
import { DEFAULT_LESSON_LOCALE } from 'constants/lessonLocale';

import { isEditorTextEmpty } from 'validations/common';

export const LESSON_LOCALIZATION_FIELDS: Record<string, string[]> = {
  [LESSON_TYPES.THEORY_LESSON]: ['title', 'blocks'],
  [LESSON_TYPES.FIGMA_LESSON]: [
    'title',
    'subtitle',
    'description',
    'goals',
    'tasks',
    'goodToKnowBlocks',
    'canvasTheory',
  ],
  [LESSON_TYPES.QUIZ]: ['title', 'questions'],
  [LESSON_TYPES.CONNECTION_QUIZ]: ['title', 'text', 'answers'],
  [LESSON_TYPES.TWO_COLUMNS_LESSON]: [
    'title',
    'text',
    'leftVariantsTitle',
    'rightVariantsTitle',
    'leftVariants',
    'rightVariants',
  ],
  [LESSON_TYPES.DIFFERENCE_LESSON]: ['title', 'text', 'leftItem', 'rightItem'],
  [LESSON_TYPES.COLOR_MATCH_LESSON]: ['title', 'text'],
};

const LOCALE_TAB_COMPLETION_FIELDS: Record<string, string[]> = {
  [LESSON_TYPES.FIGMA_LESSON]: ['tasks'],
};

const getLocaleTabCompletionFields = (lessonType: string) =>
  LOCALE_TAB_COMPLETION_FIELDS[lessonType] ??
  LESSON_LOCALIZATION_FIELDS[lessonType] ??
  [];

const getDifferenceItemAssetUrl = (item: unknown) => {
  if (!item || typeof item !== 'object') {
    return undefined;
  }

  const url = (item as Record<string, unknown>).url;

  return typeof url === 'string' && url !== '' ? url : undefined;
};

const getDifferenceItemCorrect = (item: unknown) => {
  if (!item || typeof item !== 'object') {
    return undefined;
  }

  const correct = (item as Record<string, unknown>).correct;

  return typeof correct === 'boolean' ? correct : undefined;
};

export const mergeDifferenceItemAssets = (
  sourceItem: unknown,
  targetItem: unknown,
) => {
  if (!targetItem || typeof targetItem !== 'object') {
    return sourceItem ?? targetItem;
  }

  if (!sourceItem || typeof sourceItem !== 'object') {
    return targetItem;
  }

  const sourceUrl = getDifferenceItemAssetUrl(sourceItem);
  const sourceCorrect = getDifferenceItemCorrect(sourceItem);

  return {
    ...(targetItem as Record<string, unknown>),
    url: sourceUrl ?? (targetItem as Record<string, unknown>).url,
    correct:
      sourceCorrect ?? (targetItem as Record<string, unknown>).correct,
  };
};

export const mergeDifferenceLessonAssets = (
  sourceLesson: Record<string, any> | null | undefined,
  targetLesson: Record<string, any> | null | undefined,
) => {
  if (!targetLesson) {
    return targetLesson;
  }

  if (!sourceLesson) {
    return targetLesson;
  }

  return {
    ...targetLesson,
    leftItem: mergeDifferenceItemAssets(
      sourceLesson.leftItem,
      targetLesson.leftItem,
    ),
    rightItem: mergeDifferenceItemAssets(
      sourceLesson.rightItem,
      targetLesson.rightItem,
    ),
  };
};

export const extractLessonLocalizationPayload = (
  lessonType: string,
  data: Record<string, any>,
) => {
  const payload = _pick(data, LESSON_LOCALIZATION_FIELDS[lessonType] || []);

  if (
    lessonType === LESSON_TYPES.FIGMA_LESSON &&
    Array.isArray(payload.tasks)
  ) {
    return {
      ...payload,
      tasks: payload.tasks.map((task: Record<string, any>) => ({
        ...(task?.id != null ? { id: task.id } : {}),
        text: task?.text,
      })),
    };
  }

  return payload;
};

export const getChangedLocalizationProperties = ({
  lessonType,
  currentLesson,
  nextPayload,
}: {
  lessonType: string;
  currentLesson: Record<string, any>;
  nextPayload: Record<string, any>;
}) => {
  const fields = LESSON_LOCALIZATION_FIELDS[lessonType] || [];

  return fields.filter((fieldName) => {
    const currentValue = currentLesson?.[fieldName];
    const nextValue = nextPayload[fieldName];

    return !_isEqual(currentValue, nextValue);
  });
};

export const getLocalizationPayloadByKeys = ({
  payload,
  keys,
}: {
  payload: Record<string, any>;
  keys: string[];
}) => _pick(payload, keys);

const LOCALIZATION_FIELDS_WITH_STRUCTURAL_TASKS = new Set(['tasks']);

export const isLocalizationOnlyProperty = (
  lessonType: string,
  propertyKey: string,
) => {
  const localizationFields = LESSON_LOCALIZATION_FIELDS[lessonType] || [];

  if (LOCALIZATION_FIELDS_WITH_STRUCTURAL_TASKS.has(propertyKey)) {
    return false;
  }

  return localizationFields.includes(propertyKey);
};

export const getChangedStructuralProperties = ({
  lessonType,
  changedProperties,
}: {
  lessonType: string;
  changedProperties: string[];
}) =>
  changedProperties.filter(
    (propertyKey) => !isLocalizationOnlyProperty(lessonType, propertyKey),
  );

const areStringItemsFilled = (items: unknown): boolean =>
  Array.isArray(items) &&
  items.length > 0 &&
  items.every((item) => !isEditorTextEmpty(item));

const areCanvasTheoryItemsFilled = (items: unknown): boolean =>
  Array.isArray(items) &&
  items.length > 0 &&
  items.every(
    (item) =>
      item &&
      typeof item === 'object' &&
      typeof (item as Record<string, unknown>).url === 'string' &&
      (item as Record<string, unknown>).url !== '',
  );

const areTheoryBlocksFilled = (blocks: unknown): boolean => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return false;
  }

  return blocks.every((block) => {
    if (!block || typeof block !== 'object') {
      return false;
    }

    const blockRecord = block as Record<string, unknown>;

    switch (blockRecord.type) {
      case 'heading1':
      case 'heading2':
      case 'heading3':
      case 'text':
        return !isEditorTextEmpty(blockRecord.data);
      case 'spoiler':
        return (
          !isEditorTextEmpty(blockRecord.title) &&
          areTheoryBlocksFilled(blockRecord.blocks)
        );
      case 'numberList':
      case 'markerList':
        return areStringItemsFilled(blockRecord.items);
      case 'listWithProperties':
        return (
          areStringItemsFilled(blockRecord.items) &&
          !isEditorTextEmpty(blockRecord.title)
        );
      case 'blockWithValue':
        return (
          !isEditorTextEmpty(blockRecord.title) &&
          !isEditorTextEmpty(blockRecord.value)
        );
      case 'image':
      case 'video':
        return Boolean(blockRecord.url);
      case 'divider':
        return true;
      default:
        return true;
    }
  });
};

export const areTasksFilled = (tasks: unknown): boolean =>
  Array.isArray(tasks) &&
  tasks.length > 0 &&
  tasks.every(
    (task) =>
      task &&
      typeof task === 'object' &&
      !isEditorTextEmpty((task as Record<string, unknown>).text),
  );

const areQuizQuestionsFilled = (questions: unknown): boolean => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return false;
  }

  return questions.every((question) => {
    if (!question || typeof question !== 'object') {
      return false;
    }

    const questionRecord = question as Record<string, unknown>;
    const blocks = questionRecord.blocks;
    const answers = questionRecord.answers;

    const blocksFilled =
      Array.isArray(blocks) &&
      blocks.length > 0 &&
      blocks.every(
        (block) =>
          block &&
          typeof block === 'object' &&
          !isEditorTextEmpty((block as Record<string, unknown>).data),
      );

    const answersFilled =
      Array.isArray(answers) &&
      answers.length > 0 &&
      answers.every(
        (answer) =>
          answer &&
          typeof answer === 'object' &&
          !isEditorTextEmpty((answer as Record<string, unknown>).data),
      );

    return blocksFilled && answersFilled;
  });
};

const areConnectionAnswersFilled = (answers: unknown): boolean => {
  if (!Array.isArray(answers) || answers.length === 0) {
    return false;
  }

  return answers.every(
    (answer) =>
      answer &&
      typeof answer === 'object' &&
      !isEditorTextEmpty(
        (answer as Record<string, unknown>).text ??
          (answer as Record<string, unknown>).data,
      ),
  );
};

const isLocalizationFieldFilled = (
  fieldName: string,
  value: unknown,
): boolean => {
  switch (fieldName) {
    case 'tasks':
      return areTasksFilled(value);
    case 'questions':
      return areQuizQuestionsFilled(value);
    case 'blocks':
    case 'goodToKnowBlocks':
      return areTheoryBlocksFilled(value);
    case 'canvasTheory':
      return areCanvasTheoryItemsFilled(value);
    case 'answers':
      return areConnectionAnswersFilled(value);
    case 'leftVariants':
    case 'rightVariants':
    case 'goals':
      return areStringItemsFilled(value);
    case 'leftItem':
    case 'rightItem':
      return (
        Boolean(value) &&
        typeof value === 'object' &&
        !isEditorTextEmpty((value as Record<string, unknown>).text)
      );
    default:
      return !isEditorTextEmpty(value);
  }
};

export const mergeLocalizedTaskTexts = (
  baseTasks: unknown,
  localizedTasks: unknown,
) => {
  if (!Array.isArray(baseTasks)) {
    return [];
  }

  const localizedTasksArray = Array.isArray(localizedTasks)
    ? localizedTasks
    : [];

  return baseTasks.map((baseTask, index) => {
    const baseTaskRecord = baseTask as Record<string, unknown>;
    const localizedTask = localizedTasksArray[index];
    const localizedText =
      localizedTask && typeof localizedTask === 'object'
        ? (localizedTask as Record<string, unknown>).text
        : undefined;

    return {
      ...baseTaskRecord,
      text:
        localizedText != null && !isEditorTextEmpty(localizedText)
          ? localizedText
          : [],
    };
  });
};

const mergeLocalizedTaskTextsWithBaseFallback = (
  baseTasks: unknown,
  localizedTasks: unknown,
) => {
  if (!Array.isArray(baseTasks)) {
    return [];
  }

  const localizedTasksArray = Array.isArray(localizedTasks)
    ? localizedTasks
    : [];

  return baseTasks.map((baseTask, index) => {
    const baseTaskRecord = baseTask as Record<string, unknown>;
    const localizedTask = localizedTasksArray[index];
    const localizedText =
      localizedTask && typeof localizedTask === 'object'
        ? (localizedTask as Record<string, unknown>).text
        : undefined;

    return {
      ...baseTaskRecord,
      text:
        localizedText != null && !isEditorTextEmpty(localizedText)
          ? localizedText
          : baseTaskRecord.text,
    };
  });
};

const getPayloadFieldValue = ({
  payload,
  fieldName,
  fallbackValue,
}: {
  payload: Record<string, any>;
  fieldName: string;
  fallbackValue: unknown;
}) =>
  Object.prototype.hasOwnProperty.call(payload, fieldName)
    ? payload[fieldName]
    : fallbackValue;

export const getDefaultLocaleReferenceContent = (
  lessonType: string,
  baseLesson: Record<string, any>,
  defaultLocalePayload: Record<string, any> | null | undefined,
) => {
  const fields = LESSON_LOCALIZATION_FIELDS[lessonType] || [];
  const baseContent = extractLessonLocalizationPayload(lessonType, baseLesson);
  const payload = defaultLocalePayload ?? {};

  return Object.fromEntries(
    fields.map((fieldName) => {
      if (fieldName === 'tasks') {
        return [
          fieldName,
          mergeLocalizedTaskTextsWithBaseFallback(
            baseContent.tasks,
            payload.tasks,
          ),
        ];
      }

      if (fieldName === 'leftItem' || fieldName === 'rightItem') {
        return [
          fieldName,
          mergeDifferenceItemAssets(
            baseContent[fieldName],
            getPayloadFieldValue({
              payload,
              fieldName,
              fallbackValue: baseContent[fieldName],
            }),
          ),
        ];
      }

      return [
        fieldName,
        getPayloadFieldValue({
          payload,
          fieldName,
          fallbackValue: baseContent[fieldName],
        }),
      ];
    }),
  );
};

export const getLocaleContentFromPayload = (
  lessonType: string,
  baseLesson: Record<string, any>,
  payload: Record<string, any> | null | undefined,
) => {
  const fields = LESSON_LOCALIZATION_FIELDS[lessonType] || [];
  const baseContent = extractLessonLocalizationPayload(lessonType, baseLesson);
  const localizationPayload = payload ?? {};

  return Object.fromEntries(
    fields.map((fieldName) => {
      if (fieldName === 'tasks') {
        return [
          fieldName,
          mergeLocalizedTaskTexts(
            baseContent.tasks,
            localizationPayload.tasks,
          ),
        ];
      }

      if (fieldName === 'leftItem' || fieldName === 'rightItem') {
        return [
          fieldName,
          mergeDifferenceItemAssets(
            baseContent[fieldName],
            localizationPayload[fieldName],
          ),
        ];
      }

      return [fieldName, localizationPayload[fieldName]];
    }),
  );
};

export const isLocalizationCompleteFromPayloads = (
  lessonType: string,
  targetContent: Record<string, any>,
  referenceContent: Record<string, any>,
  fieldsToCheck?: string[],
) => {
  const fields =
    fieldsToCheck ?? LESSON_LOCALIZATION_FIELDS[lessonType] ?? [];

  return fields.every((fieldName) => {
    const referenceValue = referenceContent[fieldName];

    if (!isLocalizationFieldFilled(fieldName, referenceValue)) {
      return true;
    }

    return isLocalizationFieldFilled(fieldName, targetContent[fieldName]);
  });
};

export const getLocalizationPayloadForLocale = (
  localizations: Array<{ locale: string; payload?: Record<string, any> }>,
  locale: string,
) => {
  const localization = localizations.find((item) => item.locale === locale);

  return localization?.payload ?? {};
};

export const isLocalizationCompleteForLocale = ({
  lessonType,
  locale,
  baseLesson,
  localizations,
  mergedLesson,
}: {
  lessonType: string;
  locale: string;
  baseLesson: Record<string, any>;
  localizations: Array<{ locale: string; payload?: Record<string, any> }>;
  mergedLesson?: Record<string, any> | null;
}) => {
  const defaultLocalePayload = getLocalizationPayloadForLocale(
    localizations,
    DEFAULT_LESSON_LOCALE,
  );
  const localePayload = getLocalizationPayloadForLocale(localizations, locale);
  const baseReferenceContent = extractLessonLocalizationPayload(
    lessonType,
    baseLesson,
  );
  const referenceContent =
    locale === DEFAULT_LESSON_LOCALE
      ? baseReferenceContent
      : getDefaultLocaleReferenceContent(
          lessonType,
          baseLesson,
          defaultLocalePayload,
        );
  const targetContent =
    locale === DEFAULT_LESSON_LOCALE
      ? getDefaultLocaleReferenceContent(
          lessonType,
          baseLesson,
          defaultLocalePayload,
        )
      : getLocaleContentFromPayload(lessonType, baseLesson, localePayload);

  const tabCompletionFields = getLocaleTabCompletionFields(lessonType);
  const targetContentForCheck =
    mergedLesson && tabCompletionFields.includes('tasks')
      ? {
          ...targetContent,
          tasks: mergedLesson.tasks ?? targetContent.tasks,
        }
      : targetContent;

  return isLocalizationCompleteFromPayloads(
    lessonType,
    targetContentForCheck,
    referenceContent,
    tabCompletionFields,
  );
};

export const isLocalizationComplete = (
  lessonType: string,
  lesson: Record<string, any>,
  referenceLesson: Record<string, any>,
) => {
  const payload = extractLessonLocalizationPayload(lessonType, lesson);
  const referenceContent = extractLessonLocalizationPayload(
    lessonType,
    referenceLesson,
  );

  return isLocalizationCompleteFromPayloads(
    lessonType,
    payload,
    referenceContent,
  );
};
